import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  changeClientPortalUserEmail,
  selectIsBusy,
  selectNewEmail,
  selectShowEmailForm,
  selectShowVerificationCodeForm,
  selectUserProfile,
  sendClientPortalUserEmailVerificationCode,
  setShowEmailForm,
  setShowVerificationCodeForm,
} from "./UserProfileSlice";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import OTPInput from "react-otp-input";

type EmailChangeProps = {
  newEmail: string;
};

type VerificationCodeProps = {
  verificationCode: string;
};

const emailFormValidationSchema = Yup.object().shape({
  newEmail: Yup.string()
    .email("Wrong email format")
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Email is required"),
});

const emailVerificationCodeFormValidationSchema = Yup.object().shape({
  verificationCode: Yup.string()
    .min(6, "Minimum 6 symbols")
    .max(6, "Maximum 6 symbols")
    .required("Email verification code is required"),
});

const ChangeEmailForm = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const showEmailForm = useSelector(selectShowEmailForm);
  const showVerificationCodeForm = useSelector(selectShowVerificationCodeForm);
  const user = useSelector(selectUserProfile);
  const newEmail = useSelector(selectNewEmail);

  const isBusy = useSelector(selectIsBusy);
  const updateEmailInitialValues: EmailChangeProps = {
    newEmail: "",
  };

  const handleUpdateEmailSubmit = (values: EmailChangeProps) => {
    thunkDispatch(sendClientPortalUserEmailVerificationCode({ emailAddress: values.newEmail! }));
  };

  const changeEmailInitialValues: VerificationCodeProps = {
    verificationCode: "",
  };

  const changeEmailSubmit = (values: VerificationCodeProps, formikHelpers: FormikHelpers<VerificationCodeProps>) => {
    thunkDispatch(
      changeClientPortalUserEmail({
        verificationCode: values.verificationCode,
      })
    );
    formikHelpers.resetForm();
  };

  return (
    <>
      {!showEmailForm ? (
        <div className="row align-items-end mt-2 mb-6">
          <div className="col-12 col-md-6">
            <label htmlFor="emailAddress" className="form-label">
              Email Address
            </label>
            <input
              data-test-id="change-email-form__email-address-input"
              id="emailAddress"
              className="form-control form-control-plaintext"
              value={user.emailAddress}
              readOnly
            />
          </div>
          <div className="col-12 col-md-6 mt-1">
            <button
              data-test-id="change-email-form__change-email-button"
              onClick={() => {
                dispatch(setShowEmailForm(true));
              }}
              className="cpl-btn cpl-btn-primary"
            >
              Change Email
            </button>
          </div>
        </div>
      ) : !showVerificationCodeForm ? (
        <Formik
          initialValues={updateEmailInitialValues}
          validationSchema={emailFormValidationSchema}
          onSubmit={handleUpdateEmailSubmit}
        >
          <Form>
            <div className="row align-items-end mb-6">
              <div className="col-12 col-md-6">
                <div className="row">
                  <div className="col-12 col-md-6">
                    <label htmlFor="emailaddress" className="form-label required">
                      Enter New Email Address
                    </label>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <ErrorMessage name="newEmail" />
                      </div>
                    </div>
                  </div>
                </div>
                <Field
                  data-test-id="change-email-form__new-email-input"
                  id="emailaddress"
                  type="email"
                  className="form-control form-control-solid"
                  name="newEmail"
                />
              </div>
              <div className="col-12 col-md-6 mt-1">
                <button
                  data-test-id="change-email-form__send-code-button"
                  type="submit"
                  className="cpl-btn cpl-btn-primary me-1"
                >
                  {!isBusy && "Send Code"}
                  {isBusy && (
                    <span className="indicator-progress" style={{ display: "block" }}>
                      Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </button>
                <button
                  data-test-id="change-email-form__cancel-send-code-button"
                  type="button"
                  onClick={() => {
                    dispatch(setShowEmailForm(false));
                  }}
                  className="cpl-btn cpl-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Form>
        </Formik>
      ) : (
        <Formik
          initialValues={changeEmailInitialValues}
          validationSchema={emailVerificationCodeFormValidationSchema}
          onSubmit={changeEmailSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="row align-items-end mb-6">
                <div className="col-12 col-md-6">
                  <label htmlFor="emailaddress" className="form-label">
                    <div className="text-success mb-3">We have sent a verification code to {newEmail}.</div>
                    Enter Verification Code
                  </label>
                  <OTPInput
                    inputStyle="inputStyle"
                    value={values.verificationCode}
                    onChange={(otp: string) => {
                      setFieldValue("verificationCode", otp);
                    }}
                    numInputs={6}
                    inputType="number"
                    renderSeparator={(index) => (index === 2 ? <span>-</span> : "")}
                    renderInput={(props, index) => (
                      <input {...props} data-test-id={`change-email-form__verification-code-input-${index}`} />
                    )}
                  />
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <ErrorMessage name="verificationCode" />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6 mt-1">
                  <button
                    data-test-id="change-email-form__update-email-button"
                    type="submit"
                    className="cpl-btn cpl-btn-primary me-1"
                  >
                    {!isBusy && "Update Email"}
                    {isBusy && (
                      <span className="indicator-progress" style={{ display: "block" }}>
                        Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                  <button
                    data-test-id="change-email-form__cancel-verification-button"
                    type="button"
                    onClick={() => {
                      dispatch(setShowVerificationCodeForm(false));
                      dispatch(setShowEmailForm(false));
                    }}
                    className="cpl-btn cpl-btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default ChangeEmailForm;
